import { useCallback, useEffect, useRef, useState } from 'react';
import ioClient from 'socket.io-client';
import type { ConsoleOutputEntry } from '../../TestExecution/ConsoleOutput';

interface UseSingleTestExecutionResult {
  running: boolean;
  consoleOutput: ConsoleOutputEntry[];
  testResult: string | null;
  testStatus: string | null;
  runTest: () => Promise<void>;
  stopTest: () => Promise<void>;
}

export function useSingleTestExecution(
  schemaId: string | undefined,
  filename: string | undefined,
): UseSingleTestExecutionResult {
  const [running, setRunning] = useState(false);
  const [consoleOutput, setConsoleOutput] = useState<ConsoleOutputEntry[]>([]);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<string | null>(null);
  const socketRef = useRef<ReturnType<typeof ioClient> | null>(null);

  useEffect(() => {
    if (!schemaId || !filename) return;

    const API_URL = import.meta.env.VITE_API_URL || '';
    socketRef.current = ioClient(API_URL);

    socketRef.current.on('single-test-start', (data: { schemaId: string; filename: string }) => {
      if (data.schemaId === schemaId && data.filename === filename) {
        setConsoleOutput([]);
        setTestResult(null);
        setTestStatus('running');
      }
    });

    socketRef.current.on(
      'single-test-output',
      (data: { schemaId: string; filename: string; type: string; data: string }) => {
        if (data.schemaId === schemaId && data.filename === filename) {
          setConsoleOutput((prev) => [...prev, { type: data.type, data: data.data }]);
        }
      },
    );

    socketRef.current.on(
      'single-test-complete',
      (data: { schemaId: string; filename: string; success: boolean; result: string | null; status?: string }) => {
        if (data.schemaId === schemaId && data.filename === filename) {
          setRunning(false);
          if (data.result) {
            setTestResult(data.result);
          }
          if (data.status) {
            setTestStatus(data.status);
          }
        }
      },
    );

    socketRef.current.on('single-test-error', (data: { schemaId: string; filename: string; error: string }) => {
      if (data.schemaId === schemaId && data.filename === filename) {
        setRunning(false);
        setTestStatus('error');
        setConsoleOutput((prev) => [...prev, { type: 'stderr', data: `Error: ${data.error}` }]);
      }
    });

    // Also listen for per-test result updates from the stream parser
    socketRef.current.on(
      'test-result-update',
      (data: { schemaId: string; filename: string; result: string; status: string }) => {
        if (data.schemaId === schemaId && data.filename === filename) {
          setTestResult(data.result);
          setTestStatus(data.status);
        }
      },
    );

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [schemaId, filename]);

  const runTest = useCallback(async () => {
    if (!schemaId || !filename) return;

    setRunning(true);
    setConsoleOutput([{ type: 'stdout', data: `Starting test execution: ${filename}\n` }]);
    setTestResult(null);
    setTestStatus('running');

    try {
      const API_URL = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${API_URL}/api/generate/run-single-test/${schemaId}/${filename}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        setRunning(false);
        setTestStatus('error');
        setConsoleOutput([{ type: 'stderr', data: `Error: ${data.error || 'Failed to start test'}` }]);
      }
    } catch (error: unknown) {
      setRunning(false);
      setTestStatus('error');
      const message = error instanceof Error ? error.message : String(error);
      setConsoleOutput([{ type: 'stderr', data: `Error: ${message}` }]);
    }
  }, [schemaId, filename]);

  const stopTest = useCallback(async () => {
    if (!schemaId || !filename) return;

    try {
      const API_URL = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${API_URL}/api/generate/run-single-test/${schemaId}/${filename}/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setRunning(false);
        setConsoleOutput((prev) => [...prev, { type: 'stderr', data: '\n[Test stopped by user]\n' }]);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('Failed to stop test:', message);
    }
  }, [schemaId, filename]);

  return { running, consoleOutput, testResult, testStatus, runTest, stopTest };
}
